import { cloudFunctionsAPI, auth } from "../firebase";
import { serverTimestamp } from "firebase/firestore";
import { generateMutualMatchQueue, regenerateMutualMatchQueue } from "../utils/mutualMatchQueue";
import MixerScheduler from "../utils/mixerSchedulerQueue";
import { flattenMatrix, reconstructMatrix } from "../utils/createMatrix";

export const callCloudFunction = async (functionName, params) => {
  const functionUrl = cloudFunctionsAPI.getUrl(
    functionName,
    cloudFunctionsAPI.locationID
  );

  const body = JSON.stringify(params);

  try {
    // Identify this caller to the backend: updateCurrentRound now accepts the
    // event's host or a superAdmin (verified server-side via this ID token), or
    // the Cloud Tasks shared secret. Without it the backend treats the call as
    // an anonymous forgery once enforcement is on.
    const idToken = await auth.currentUser?.getIdToken();
    const res = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body,
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    return res;
  } catch (error) {
    // Rethrow: swallowing here returned undefined and made every failed call
    // look like a success to the caller. Callers own the user-facing handling.
    console.error("Error calling Firebase function:", error);
    throw error;
  }
};

// Helper functions
export const getQueueLength = (eventData) => {
  if (eventData.queue?.queueMatrix) {
    const queueMatrixKeys = Object.keys(eventData.queue.queueMatrix);
    return Math.max(...queueMatrixKeys.map(key => parseInt(key.split('_')[0]))) + 1;
  }
  return 0;
};

export const updateEventState = (eventData) => ({
  id: eventData?.id || null,
  queue: eventData?.queue || {},
  activeSession: eventData?.activeSession || {},
  currentQueue: eventData?.queue,
  movers: eventData?.movers,
  sitters: eventData?.sitters,
  rsvps: eventData?.rsvps || {}
});

export const hasUsers = (group) => Array.isArray(group) && group.length > 0;

export const groupHasUsers = (group) =>
  group && (hasUsers(group.movers) || hasUsers(group.sitters));

// Regenerate queue with late comers. Removals are handled inside the matrix
// builders (queue.removedUsers / removedMovers / removedSitters), not here —
// both live queue shapes keep the original ID lists as index references, so
// nulling entries out of them would corrupt the matrix mapping.
export const buildRegeneratedQueue = async (event, eventData) => {
  let newQueue = { ...event.queue };
  if (newQueue) {
    try {
      if (eventData.eventType == 0 && eventData.groupCount == 1) {
        const incomingSitters = event.sitters.filter(uid => !event.queue.sitters.includes(uid));
        const sitters = [...event.queue.sitters, ...incomingSitters];

        const oldQueueMatrix = reconstructMatrix(newQueue.queueMatrix);
        const sittersCount = event.sitters.length - incomingSitters.length;

        const removedUsers = newQueue.removedUsers || [];
        const removedUsersIndices = removedUsers
          .map(userID => event.sitters.findIndex(id => id === userID))
          .filter(index => index !== -1);

        const mixer = new MixerScheduler(sittersCount, oldQueueMatrix, removedUsersIndices);
        const prioritizations = [];
        for (const [userID, userData] of Object.entries(eventData.eventUsers)) {
          if (userData.prioritize && Array.isArray(userData.prioritize)) {
            const userIndex = sitters.indexOf(userID);
            if (userIndex !== -1) {
              for (const prioID of userData.prioritize) {
                const prioIndex = sitters.indexOf(prioID);
                if (prioIndex !== -1) {
                  prioritizations.push([userIndex, prioIndex]);
                }
              }
            }
          }
        }
        mixer.updateScheduleWithNewParticipants(eventData.activeSession.currentRound, incomingSitters, prioritizations);

        const flattenedQueueMatrix = flattenMatrix(mixer.queueMatrix);
        const queueMatrix = mixer.removeDuplicateMeetings(flattenedQueueMatrix);

        newQueue = {
          at: serverTimestamp(),
          queueMatrix,
          queueMatrixRaw: flattenedQueueMatrix,
          sitters,
          removedUsers
        };
      }
      else {
        newQueue = await regenerateMutualMatchQueue(
          event,
          eventData.activeSession.currentRound,
          eventData
        );
      }
    } catch (error) {
      alert(error.message);
    }
  }

  return newQueue;
};

export const buildGeneratedQueue = async (event, eventData) => {
  const movers = [...event.movers];
  const sitters = [...event.sitters];
  let newQueue;
  try {
    if (eventData.eventType == 0 && eventData.groupCount == 1) {

      const mixer = new MixerScheduler(sitters.length);
      mixer.generateSchedule()

      const flattenedQueueMatrix = flattenMatrix(mixer.queueMatrix);
      const queueMatrix = mixer.removeDuplicateMeetings(flattenedQueueMatrix);


      newQueue = {
        at: serverTimestamp(),
        queueMatrix,
        queueMatrixRaw: flattenedQueueMatrix,
        sitters
      };
    }
    else {
      newQueue = await generateMutualMatchQueue(
        movers,
        sitters,
        eventData
      );
    }
  } catch (error) {
    alert(error.message);
  }

  return newQueue;
};
